<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="fileUploadModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="fileUploadModalTitle">Datei hochladen</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen" :disabled="uploading"></button>
        </div>

        <div class="modal-body">
          <p class="mb-2">Was möchtest du hochladen?</p>
          <div class="form-check" v-for="opt in categories" :key="opt.value">
            <input
              :id="`cat-${opt.value}`"
              class="form-check-input"
              type="radio"
              :value="opt.value"
              v-model="category"
              :disabled="uploading"
            />
            <label class="form-check-label" :for="`cat-${opt.value}`">{{ opt.label }}</label>
          </div>

          <div v-if="category === 'anderes'" class="mt-2">
            <label class="form-label" for="file-name">Bezeichnung</label>
            <input
              id="file-name"
              v-model="name"
              type="text"
              class="form-control"
              placeholder="z. B. Garantiebeleg"
              :disabled="uploading"
            />
          </div>

          <div class="mt-3">
            <label class="btn btn-outline-secondary btn-sm file-pick-btn" :class="{ disabled: uploading || !category }">
              {{ category === 'anderes' ? 'Datei wählen' : 'Dateien wählen' }}
              <input
                type="file"
                accept="image/*,.heic,.heif,application/pdf"
                :multiple="category !== 'anderes'"
                class="d-none"
                :disabled="uploading || !category"
                @change="onPick"
              />
            </label>
            <ul v-if="selectedFiles.length" class="picked-list mt-2">
              <li v-for="(f, i) in selectedFiles" :key="i" class="picked-item">{{ f.name }}</li>
            </ul>
          </div>

          <p v-if="error" class="error mt-2 mb-0">{{ error }}</p>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="uploading">Abbrechen</button>
          <button type="button" class="btn btn-primary" :disabled="!canUpload || uploading" @click="onUpload">
            {{ uploading ? 'Lädt…' : 'Hochladen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { uploadFile, type FileCategory } from '../services/tickets';

/**
 * Modal zum Hochladen einer/mehrerer Dateien an ein Ticket.
 * Zuerst Kategorie wählen (Artikelfoto/Rechnung → automatisch nummeriert serverseitig;
 * Anderes Dokument → Pflicht-Bezeichnung). Aufruf via `open(ticketId)`; Promise löst mit
 * `true`, wenn mindestens eine Datei hochgeladen wurde, sonst `false`.
 */
const MAX_BATCH = 5;
// "Rechnung" ist derzeit deaktiviert (nur Artikelfoto + Anderes Dokument auswählbar).
const categories: { value: FileCategory; label: string }[] = [
  { value: 'artikelfoto', label: 'Artikelfoto' },
  { value: 'anderes',     label: 'Anderes Dokument' },
];

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((changed: boolean) => void) | null = null;
let   changed = false;

const ticketId      = ref(0);
const category      = ref<FileCategory | ''>('');
const name          = ref('');
const selectedFiles = ref<File[]>([]);
const uploading     = ref(false);
const error         = ref<string | null>(null);

const canUpload = computed(() => {
  if (!category.value || selectedFiles.value.length === 0) return false;
  if (category.value === 'anderes' && name.value.trim() === '') return false;
  return true;
});

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value, { backdrop: 'static' });
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(id: number): Promise<boolean> {
  ticketId.value      = id;
  category.value      = '';
  name.value          = '';
  selectedFiles.value = [];
  uploading.value     = false;
  error.value         = null;
  changed             = false;
  return new Promise<boolean>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function onPick(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  error.value = null;
  if (category.value === 'anderes') {
    selectedFiles.value = files.slice(0, 1);
  } else {
    if (files.length > MAX_BATCH) {
      error.value = `Pro Upload sind maximal ${MAX_BATCH} Dateien möglich – nur die ersten ${MAX_BATCH} werden übernommen.`;
    }
    selectedFiles.value = files.slice(0, MAX_BATCH);
  }
}

async function onUpload(): Promise<void> {
  if (!canUpload.value || uploading.value || !category.value) return;
  uploading.value = true;
  error.value     = null;
  try {
    const customName = category.value === 'anderes' ? name.value.trim() : undefined;
    for (const file of selectedFiles.value) {
      await uploadFile(ticketId.value, file, category.value, customName);
      changed = true;
    }
    modal?.hide();
  } catch (e) {
    error.value = e instanceof Error ? `Upload fehlgeschlagen: ${e.message}` : 'Upload fehlgeschlagen.';
    uploading.value = false;
  }
}

function onHidden(): void {
  resolver?.(changed);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.file-pick-btn {
  margin: 0;
  cursor: pointer;
}

.file-pick-btn.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.picked-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.picked-item {
  font-size: 0.9rem;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
